import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Clock,
  Download,
  Loader2,
  PenLine,
  RefreshCcw,
  TrendingUp,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  baixarCsv,
  usePainel,
  CATEGORIAS_CSV,
  type CategoriaCsv,
  type FiltrosDashboard,
  type NivelOcupacao,
  type Painel,
} from "@/api/dashboard";
import { listarProfissionais, parametro } from "@/api/registry";
import { ROTULO_ETAPA } from "@/api/projetos";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard")({
  component: DashboardPage,
});

/**
 * M3 — Dashboard contra a operação real.
 *
 * Todos os painéis vêm calculados do servidor. No MVP a maior parte lia campos
 * estáticos do mock — `slidesProduzidos`, `horasConteudoGastas`, `health` —,
 * de modo que o Dashboard contava uma história que a grade não confirmava.
 */
function DashboardPage() {
  const [filtros, setFiltros] = useState<FiltrosDashboard>({});
  const [categoria, setCategoria] = useState<CategoriaCsv>("coordenadores");
  const painel = usePainel(filtros);

  const frentes = parametro<string[]>("frentes", []);
  const tiposProjeto = parametro<string[]>("tiposProjeto", []);
  const coordenadores = listarProfissionais().filter((p) => p.funcao === "Coordenador de Projetos");

  const ajustar = (campo: keyof FiltrosDashboard, valor: string) =>
    setFiltros((f) => ({ ...f, [campo]: valor || undefined }));

  const dados = painel.data;

  return (
    <>
      <TopBar
        title="Dashboard de Produção"
        subtitle={
          dados
            ? `${dados.recorte.projetos} projetos no recorte · taxa de reprogramação ${dados.indicadores.taxaReprogramacao}%`
            : "Carregando indicadores…"
        }
      />

      <div className="flex-1 space-y-4 overflow-auto p-4">
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3">
          <Filtro label="Frente">
            <Select
              value={filtros.frente ?? ""}
              onChange={(v) => ajustar("frente", v)}
              options={[
                { value: "", label: "Todas" },
                ...frentes.map((f) => ({ value: f, label: f })),
              ]}
            />
          </Filtro>
          <Filtro label="Tipo de entrega">
            <Select
              value={filtros.tipoProjeto ?? ""}
              onChange={(v) => ajustar("tipoProjeto", v)}
              options={[
                { value: "", label: "Todos" },
                ...tiposProjeto.map((t) => ({ value: t, label: t })),
              ]}
            />
          </Filtro>
          <Filtro label="Coordenador">
            <Select
              value={filtros.coordenadorId ?? ""}
              onChange={(v) => ajustar("coordenadorId", v)}
              options={[
                { value: "", label: "Todos" },
                ...coordenadores.map((c) => ({ value: c.id, label: c.nome })),
              ]}
            />
          </Filtro>
          <Filtro label="Prazo de">
            <input
              type="date"
              value={filtros.de ?? ""}
              onChange={(e) => ajustar("de", e.target.value)}
              className={inputCls}
            />
          </Filtro>
          <Filtro label="até">
            <input
              type="date"
              value={filtros.ate ?? ""}
              onChange={(e) => ajustar("ate", e.target.value)}
              className={inputCls}
            />
          </Filtro>

          <div className="ml-auto flex items-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setFiltros({})}
            >
              Limpar
            </Button>
            {/* RF-16 — a exportação é por categoria, não um CSV plano único. */}
            <Filtro label="Exportar">
              <Select
                value={categoria}
                onChange={(v) => setCategoria(v as CategoriaCsv)}
                options={CATEGORIAS_CSV.map((c) => ({ value: c.valor, label: c.rotulo }))}
              />
            </Filtro>
            <Button
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() =>
                baixarCsv(categoria, filtros).catch(() => toast.error("Falha ao exportar"))
              }
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
          </div>
        </div>

        {painel.isPending && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Consolidando a operação…
          </div>
        )}

        {dados && <Paineis dados={dados} />}
      </div>
    </>
  );
}

function Paineis({ dados }: { dados: Painel }) {
  const i = dados.indicadores;
  const acimaDaMeta = i.taxaReprogramacao > i.metaReprogramacao;

  return (
    <>
      {/* RF-24 — cascata derivada do status real das fases. */}
      {dados.alertasCascata.length > 0 && (
        <div className="space-y-2">
          {dados.alertasCascata.map((a) => (
            <Link
              key={a.projetoId}
              to="/card/$id"
              params={{ id: a.projetoId }}
              className="flex items-center gap-2 rounded-lg border border-health-red/40 bg-health-red/10 px-3 py-2 hover:bg-health-red/15"
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-health-red" />
              <span className="text-[11px] text-foreground">
                <strong>{a.os}</strong> — {ROTULO_ETAPA[a.faseAtrasada] ?? a.faseAtrasada} atrasada,
                com {a.dependentes} fase(s) dependente(s) ainda aguardando.
              </span>
            </Link>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Kpi
          icon={<TrendingUp className="h-4 w-4" />}
          label="Projetos ativos"
          value={i.projetosAtivos}
          sub={`${i.totalNoRecorte} no recorte · ${i.pausados} pausado(s)`}
          tone="primary"
        />
        <Kpi
          icon={<RefreshCcw className="h-4 w-4" />}
          label="Taxa de reprogramação"
          value={`${i.taxaReprogramacao}%`}
          sub={`meta até ${i.metaReprogramacao}% · ${i.reprogramacoesPrazoFinal} vez(es) o prazo final moveu`}
          tone={acimaDaMeta ? "red" : "green"}
        />
        <Kpi
          icon={<PenLine className="h-4 w-4" />}
          label="Responsabilidade"
          value={`${i.reprogramacoesSoap} / ${i.reprogramacoesCliente}`}
          sub="SOAP / Cliente"
          tone="purple"
        />
        <Kpi
          icon={<Clock className="h-4 w-4" />}
          label="Horas previstas × usadas"
          value={`${i.horasUtilizadas}h`}
          sub={`de ${i.horasPrevistas}h previstas · ${i.percentualHoras}%`}
          tone="green"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Secao titulo="Consolidado mensal por frente" subtitulo="RF-21 · produção confirmada">
          {dados.consolidadoMensal.length === 0 ? (
            <Vazio>Nenhum projeto com prazo final no recorte.</Vazio>
          ) : (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-1.5 font-medium">Mês</th>
                  <th className="pb-1.5 font-medium">Frente</th>
                  <th className="pb-1.5 text-right font-medium">Projetos</th>
                  <th className="pb-1.5 text-right font-medium">Slides</th>
                  <th className="pb-1.5 text-right font-medium">Horas</th>
                </tr>
              </thead>
              <tbody>
                {dados.consolidadoMensal.map((m) =>
                  m.frentes.map((f, idx) => (
                    <tr key={`${m.mes}-${f.frente}`} className="border-b border-border/50">
                      <td className="py-1.5 font-medium text-foreground">
                        {idx === 0 ? m.mes : ""}
                      </td>
                      <td className="py-1.5 text-muted-foreground">{f.frente}</td>
                      <td className="py-1.5 text-right tabular-nums text-foreground">
                        {f.projetos}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-foreground">{f.slides}</td>
                      <td className="py-1.5 text-right tabular-nums text-foreground">{f.horas}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          )}
        </Secao>

        <Secao titulo="Carga por Coordenador" subtitulo="RF-19 · slides e horas confirmados">
          {dados.cargaCoordenador.length === 0 ? (
            <Vazio>Nenhum projeto com coordenador no recorte.</Vazio>
          ) : (
            <div className="space-y-2">
              {dados.cargaCoordenador.map((c) => (
                <div
                  key={c.coordenador.id}
                  className="flex items-center gap-2 rounded-lg border border-border bg-secondary/30 p-2.5"
                >
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: c.coordenador.cor ?? "#94A3B8" }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-xs font-semibold text-foreground">
                        {c.coordenador.nome}
                      </span>
                      {c.volumeAlto && (
                        <span className="rounded-md bg-health-red/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-health-red">
                          Volume alto
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {c.projetos} projetos · {c.slides} slides · {c.horas}h · {c.reprogramacoes}{" "}
                      reprogramação(ões)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Secao>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Secao
          titulo="Ocupação por profissional"
          subtitulo={`RF-20 · ${dados.recorte.inicioJanela} a ${dados.recorte.fimJanela}`}
        >
          <div className="space-y-1.5">
            {dados.ocupacao.map((o) => (
              <div key={o.profissional.id} className="flex items-center gap-2">
                <span className="w-32 shrink-0 truncate text-[11px] text-foreground">
                  {o.profissional.nome}
                </span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn("h-full rounded-full", corDoNivel(o.nivel))}
                    style={{ width: `${Math.min(100, o.percentual)}%` }}
                  />
                </div>
                <span className="w-24 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">
                  {o.ocupados}/{o.disponiveis} · {o.percentual}%
                </span>
              </div>
            ))}
          </div>
        </Secao>

        {/* RF-20 — o MVP tinha `futureCapacity()` pronta e zero chamadas. */}
        <Secao
          titulo="Capacidade futura"
          subtitulo={`projeção semana a semana · ${dados.recorte.horizonteSemanas} semanas`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-1.5 font-medium">Profissional</th>
                  {dados.capacidadeFutura[0]?.semanas.map((s) => (
                    <th key={s.semana} className="pb-1.5 text-right font-medium">
                      {s.semana.slice(8)}/{s.semana.slice(5, 7)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dados.capacidadeFutura.map((c) => (
                  <tr key={c.profissional.id} className="border-b border-border/50">
                    <td className="py-1.5 text-foreground">{c.profissional.nome}</td>
                    {c.semanas.map((s) => (
                      <td key={s.semana} className="py-1.5 text-right">
                        <span
                          title={`${s.ocupados} de ${s.teto} slots`}
                          className={cn(
                            "inline-block rounded px-1.5 py-0.5 tabular-nums",
                            s.percentual >= 100
                              ? "bg-health-red/15 text-health-red"
                              : s.percentual >= 80
                                ? "bg-health-yellow/15 text-health-yellow"
                                : "text-muted-foreground",
                          )}
                        >
                          {s.percentual}%
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Secao>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Secao titulo="Interno × Externo" subtitulo="dias-profissional alocados na janela">
          <div className="flex items-center gap-3">
            <div className="h-3 flex-1 overflow-hidden rounded-full bg-health-purple/20">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${dados.internoExterno.percentualInterno}%` }}
              />
            </div>
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {dados.internoExterno.interno} interno · {dados.internoExterno.externo} externo
            </span>
          </div>
        </Secao>

        <Secao titulo="Roteiristas" subtitulo="períodos ativos e horas planejadas × confirmadas">
          {dados.roteiristas.length === 0 ? (
            <Vazio>Nenhum período de roteirista na janela.</Vazio>
          ) : (
            <div className="space-y-1.5">
              {dados.roteiristas.map((r) => (
                <div
                  key={r.profissional.id}
                  className="flex items-center justify-between rounded-md border border-border bg-secondary/30 px-2.5 py-1.5"
                >
                  <span className="flex items-center gap-1.5 text-[11px] text-foreground">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    {r.profissional.nome}
                  </span>
                  <span className="text-[10px] tabular-nums text-muted-foreground">
                    {r.periodosAtivos} período(s) · {r.horasRealizadas}h de {r.horasPlanejadas}h
                  </span>
                </div>
              ))}
            </div>
          )}
        </Secao>
      </div>

      <Secao titulo="Reprogramações registradas" subtitulo="no recorte selecionado">
        {dados.reprogramacoes.length === 0 ? (
          <Vazio>Nenhuma reprogramação no recorte.</Vazio>
        ) : (
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="pb-1.5 font-medium">OS</th>
                <th className="pb-1.5 font-medium">Etapa</th>
                <th className="pb-1.5 font-medium">Nova data</th>
                <th className="pb-1.5 font-medium">Responsável</th>
                <th className="pb-1.5 font-medium">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {dados.reprogramacoes.map((r, idx) => (
                <tr key={`${r.projetoId}-${idx}`} className="border-b border-border/50">
                  <td className="py-1.5">
                    <Link
                      to="/card/$id"
                      params={{ id: r.projetoId }}
                      className="text-foreground hover:text-primary"
                    >
                      {r.os}
                    </Link>
                  </td>
                  <td className="py-1.5 text-muted-foreground">
                    {ROTULO_ETAPA[r.etapa] ?? r.etapa}
                  </td>
                  <td className="py-1.5 tabular-nums text-muted-foreground">{r.dataPara}</td>
                  <td className="py-1.5">
                    <span
                      className={cn(
                        "rounded-md px-1.5 py-0.5 text-[10px] font-medium",
                        r.responsavel === "SOAP"
                          ? "bg-health-red/15 text-health-red"
                          : "bg-secondary text-muted-foreground",
                      )}
                    >
                      {r.responsavel === "SOAP" ? "SOAP" : "Cliente"}
                    </span>
                  </td>
                  <td className="py-1.5 text-muted-foreground">{r.motivo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Secao>
    </>
  );
}

const corDoNivel = (nivel: NivelOcupacao) =>
  nivel === "CRITICO" ? "bg-health-red" : nivel === "ATENCAO" ? "bg-health-yellow" : "bg-primary";

const inputCls =
  "h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring";

function Filtro({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Secao({
  titulo,
  subtitulo,
  children,
}: {
  titulo: string;
  subtitulo?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h3 className="text-sm font-semibold text-foreground">{titulo}</h3>
      {subtitulo && <p className="mb-3 mt-0.5 text-[11px] text-muted-foreground">{subtitulo}</p>}
      {children}
    </section>
  );
}

function Vazio({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-dashed border-border p-4 text-center text-[11px] text-muted-foreground">
      {children}
    </p>
  );
}

function Kpi({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub: string;
  tone: "primary" | "green" | "red" | "purple";
}) {
  const toneCls = {
    primary: "bg-primary/15 text-primary",
    green: "bg-health-green/15 text-health-green",
    red: "bg-health-red/15 text-health-red",
    purple: "bg-health-purple/15 text-health-purple",
  }[tone];
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <div className={cn("grid h-7 w-7 place-items-center rounded-md", toneCls)}>{icon}</div>
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight text-foreground">{value}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}
